import { useWebSocketGetCalls, useOngoingCallsReport } from '@features/Admin/Campaign/hooks';
import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import { REPORT_COLUMNS } from '@features/Admin/Campaign/constants/reportColumns.constants';
import { areCallsProcessing } from '@features/Admin/Campaign/utils/ongoing-calls.utils';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CampaignDto,
  CampaignType,
  useCampaignMetricCallDurationControllerCalculateCallDurationIqrQuery,
  useCampaignCallHangupControllerCampaignCallHangupMutation,
} from '@shared/services/apiService/apiService';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { InterquartileRangeType } from '@shared/services/apiService/missing-types';
import { useDynamicTableHeight } from '@shared/hooks/useDynamicTableHeight';
import { MonitorCampaignPopupV2 } from '@widgets/Popup/MonitorCampaignV2/MonitorCampaignV2';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { SortableRecipientKeys, SortConfig } from '../types';
import { CallsToRecipientsMapper, getMedianCallDurationInMin } from '../utils';
import { useStyles } from '../styles';
import { OngoingCallsToolbar } from './components/OngoingCallsToolbar';
import { OngoingCallsTable } from './components/OngoingCallsTable';
import { ReportColumnsDialog } from './components/ReportColumnsDialog';
import { RecordingPreview } from '@pages/RecordingPreview';

interface OngoingCallsTabProps {
  campaignId: string | null;
  campaignData: CampaignDto;
  campaignType?: CampaignType;
  initialCallsCount?: number;
  onCallsCountChange?: (count: number) => void;
}

// Only this view is restored after remount; filtered totals must not replace its cached value.
const hasDefaultCallsFilters = (filters: CallsFilterFormValue) =>
  !filters.numberSearch &&
  !filters.callIdSearch &&
  filters.types === 'all' &&
  filters.statuses === 'all' &&
  filters.endedReason === 'all' &&
  !filters.start &&
  !filters.end;

const OngoingCallsTab: React.FC<OngoingCallsTabProps> = ({
  campaignId,
  campaignData,
  initialCallsCount,
  onCallsCountChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const headerRef = useRef<HTMLDivElement | null>(null);
  const tableRegionRef = useRef<HTMLDivElement | null>(null);
  const medianFooterRef = useRef<HTMLDivElement | null>(null);
  const loadMoreAnchorRef = useRef<HTMLDivElement | null>(null);
  const hasUserScrolledRef = useRef(false);

  const [showMonitorDialogFlag, setShowMonitorDialogFlag] = useState(false);
  const [isExportReportAvailable, setIsExportReportAvailable] = useState(false);
  const [recipientId, setRecipientId] = useState<string>('');
  const [callsPhoneNumber, setCallsPhoneNumber] = useState('');
  const [interruptCallId, setInterruptCallId] = useState<string | null>(null);
  const [selectedRecordingCallId, setSelectedRecordingCallId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });
  const [isExtendedSearch, setIsExtendedSearch] = useState(false);
  const [hangupCall, { isLoading: isInterruptingCall }] =
    useCampaignCallHangupControllerCampaignCallHangupMutation();

  const formMethods = useForm<CallsFilterFormValue>({
    mode: 'all',
    defaultValues: {
      numberSearch: '',
      callIdSearch: '',
      types: 'all',
      statuses: 'all',
      endedReason: 'all',
      sentimentIndicator: 'all',
      start: null,
      end: null,
    },
  });

  const handleExtendedSearchChange = (value: boolean) => {
    setIsExtendedSearch(value);
    if (!value) {
      formMethods.setValue('callIdSearch', '');
    }
  };

  const filterFormData = useWatch({ control: formMethods.control });

  const {
    isConnected: isGetCallsSocketConnected,
    recipients: CallsRecipients,
    subcribeToCalls: subcribeToGetCalls,
    recipientRefetch,
    hasMore,
    loadMore,
    totalItems,
    isLoading: isCallsLoading,
    isFetching: isCallsFetching,
  } = useWebSocketGetCalls(campaignId as string, filterFormData, sortConfig);

  const { data: InterquartileRange } =
    useCampaignMetricCallDurationControllerCalculateCallDurationIqrQuery(
      { id: campaignId as string },
      { skip: !campaignId },
    );

  const {
    showReportColumnsDialog,
    selectedAdditionalColumns,
    separatedAnalysisSummary,
    setSeparatedAnalysisSummary,
    handleReportColumnsDialogOpen,
    handleReportColumnsDialogClose,
    handleAdditionalColumnToggle,
    handleExportWithColumns,
  } = useOngoingCallsReport({ campaignId, filterFormData });

  useEffect(() => {
    if (!campaignId || !isGetCallsSocketConnected) return;
    subcribeToGetCalls(campaignId);
  }, [campaignId, isGetCallsSocketConnected, subcribeToGetCalls]);

  useEffect(() => {
    hasUserScrolledRef.current = false;
  }, [campaignId, filterFormData, sortConfig]);

  const calls = useMemo(() => CallsToRecipientsMapper(CallsRecipients ?? []), [CallsRecipients]);
  const displayedCallsCount =
    totalItems ?? (calls.length > 0 ? calls.length : (initialCallsCount ?? 0));

  useEffect(() => {
    if (totalItems !== undefined && hasDefaultCallsFilters(filterFormData)) {
      onCallsCountChange?.(totalItems);
    }
  }, [filterFormData, onCallsCountChange, totalItems]);

  useEffect(() => {
    if (calls.length) {
      setIsExportReportAvailable(areCallsProcessing(calls));
    }
  }, [calls]);

  const tryLoadMore = useCallback(() => {
    if (!hasMore || isCallsFetching || isCallsLoading || !hasUserScrolledRef.current) return;
    void loadMore();
  }, [hasMore, isCallsFetching, isCallsLoading, loadMore]);

  useEffect(() => {
    const anchor = loadMoreAnchorRef.current;
    if (!anchor || !hasMore || isCallsLoading) return;

    const scrollRoot = tableRegionRef.current?.querySelector(
      '.MuiTableContainer-root',
    ) as HTMLElement | null;

    const onScroll = () => {
      hasUserScrolledRef.current = true;
    };

    scrollRoot?.addEventListener('scroll', onScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) tryLoadMore();
      },
      { root: scrollRoot, rootMargin: '240px' },
    );

    observer.observe(anchor);
    return () => {
      observer.disconnect();
      scrollRoot?.removeEventListener('scroll', onScroll);
    };
  }, [hasMore, tryLoadMore, calls.length, isCallsLoading]);

  const handleMonitorClick = (rowId: string, phone: string) => {
    setShowMonitorDialogFlag(true);
    setRecipientId(rowId);
    setCallsPhoneNumber(phone);
  };

  const handleRecordingClick = (callId: string) => {
    setSelectedRecordingCallId(callId);
  };

  const closeMonitorDialog = () => {
    setShowMonitorDialogFlag(false);
  };

  const closeInterruptDialog = () => {
    if (isInterruptingCall) return;
    setInterruptCallId(null);
  };

  const handleInterruptClick = (callId: string) => {
    setInterruptCallId(callId);
  };

  const handleInterruptConfirm = async () => {
    if (!campaignId || !interruptCallId) return;

    try {
      await hangupCall({ id: campaignId, callId: interruptCallId }).unwrap();
      setInterruptCallId(null);
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('campaignPage.ongoingCallsTable.interrupt.successTitle'),
          description: t('campaignPage.ongoingCallsTable.interrupt.successDescription'),
        },
      });
      void recipientRefetch();
    } catch {
      toast.error(
        (props) => (
          <CustomErrorToast
            {...props}
            message={t('campaignPage.ongoingCallsTable.interrupt.errorDescription')}
            title={t('campaignPage.ongoingCallsTable.interrupt.errorTitle')}
          />
        ),
        {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        },
      );
    }
  };

  const handleSort = (key: SortableRecipientKeys, fixedDirection?: 'asc' | 'desc') => {
    let direction: 'asc' | 'desc' = fixedDirection || 'asc';
    if (!fixedDirection && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const currentRecipient = CallsRecipients?.find((recipient) => recipient.id === recipientId);

  const tableMaxHeightPx = useDynamicTableHeight({
    enabled: calls.length > 0,
    tableRegionRef,
    headerRef,
    footerRef: medianFooterRef,
  });

  const medianCallDurationLabel = getMedianCallDurationInMin(
    (InterquartileRange as InterquartileRangeType)?.iqr,
  );

  return (
    <Box sx={{ padding: '30px', width: '100%' }}>
      <OngoingCallsToolbar
        headerRef={headerRef}
        campaignData={campaignData}
        callsCount={displayedCallsCount}
        isExportReportAvailable={isExportReportAvailable}
        onOpenReportDialog={handleReportColumnsDialogOpen}
        formMethods={formMethods}
        classes={classes}
        isExtendedSearch={isExtendedSearch}
        onExtendedSearchChange={handleExtendedSearchChange}
      />
      <OngoingCallsTable
        campaignId={campaignId}
        calls={calls}
        onSort={handleSort}
        onMonitorClick={handleMonitorClick}
        onInterruptClick={handleInterruptClick}
        interruptCallId={interruptCallId}
        onInterruptDialogClose={closeInterruptDialog}
        onInterruptConfirm={handleInterruptConfirm}
        isInterruptingCall={isInterruptingCall}
        tableRegionRef={tableRegionRef}
        medianFooterRef={medianFooterRef}
        loadMoreAnchorRef={loadMoreAnchorRef}
        tableMaxHeightPx={tableMaxHeightPx}
        medianCallDurationLabel={medianCallDurationLabel}
        classes={classes}
        isExtendedSearch={isExtendedSearch}
        isLoading={isCallsLoading}
        isFetchingMore={isCallsFetching && calls.length > 0}
        onRecordingClick={handleRecordingClick}
      />
      {selectedRecordingCallId && (
        <RecordingPreview
          callId={selectedRecordingCallId}
          onClose={() => setSelectedRecordingCallId(null)}
        />
      )}
      {showMonitorDialogFlag && currentRecipient && (
        <MonitorCampaignPopupV2
          showDialogFlag={showMonitorDialogFlag}
          closeDialog={closeMonitorDialog}
          phoneNumber={callsPhoneNumber}
          recipient={currentRecipient}
        />
      )}
      <ReportColumnsDialog
        open={showReportColumnsDialog}
        onClose={handleReportColumnsDialogClose}
        onExport={handleExportWithColumns}
        reportColumns={REPORT_COLUMNS}
        selectedAdditionalColumns={selectedAdditionalColumns}
        onToggleColumn={handleAdditionalColumnToggle}
        separatedAnalysisSummary={separatedAnalysisSummary}
        onSeparatedAnalysisSummaryChange={setSeparatedAnalysisSummary}
      />
    </Box>
  );
};

export default OngoingCallsTab;
